/**
 * Tiny fetch wrapper for /api/* endpoints. Always sends the session
 * cookie (`credentials: 'include'`) and parses JSON. Throws on non-2xx
 * so TanStack Query / try-catch can react.
 */
export type ApiInit = Omit<RequestInit, 'body'> & { body?: unknown };

export async function api<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
    const headers = new Headers(init.headers || {});
    let body: BodyInit | undefined;
    if (init.body !== undefined && init.body !== null) {
        if (typeof init.body === 'string' || init.body instanceof FormData) {
            body = init.body as BodyInit;
        } else {
            headers.set('Content-Type', 'application/json');
            body = JSON.stringify(init.body);
        }
    }
    // Double-submit CSRF token: echo the frvb_csrf cookie back as a
    // header on every mutating request. The server's middleware requires
    // they match. Same-origin only, so reading document.cookie is safe.
    const method = (init.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
        const csrf = readCookie('frvb_csrf');
        if (csrf && !headers.has('X-CSRF-Token')) {
            headers.set('X-CSRF-Token', csrf);
        }
    }
    const res = await fetch('/api' + path, {
        ...init,
        body,
        headers,
        credentials: 'include'
    });
    if (!res.ok) {
        let detail = res.statusText;
        let rawBody = '';
        try {
            rawBody = await res.clone().text();
            const j = rawBody ? JSON.parse(rawBody) : null;
            if (j && typeof j.error === 'string') detail = j.error;
        } catch { /* not JSON — keep rawBody */ }
        // Surface the full server response in the console when a request
        // fails. The default Network panel preview is sometimes ambiguous
        // (e.g. nginx HTML 500 vs Slim JSON error); this makes it obvious.
        console.error('[api ✗]', method, '/api' + path, res.status, detail, rawBody);
        throw new ApiError(res.status, detail);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
}

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
        this.name = 'ApiError';
    }
}

function readCookie(name: string): string | null {
    const prefix = name + '=';
    for (const part of document.cookie.split('; ')) {
        if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
    }
    return null;
}

export function useApi() {
    return { api, ApiError };
}
